import Vue from "vue";
import operationService from "../services/operation.service";
import { $handleError, $updateOrAdd, $deleteFromArray } from '@/utils';
import { isAbortError } from '@/utils/isAbortError';
import { ForgeAnnotation, ForgeScaffold } from "@/scripts/js/forgeModels";

export const OPERATION_VIEW = {
	namespaced: true,
	state: {
		searchText: "",
		entries: [],
		total: 0,
		loading: false,
		updateLoading: false,
		page: 1,
		itemsPerPage: 10,
		selectedItems: [],
		lastOpenedItemId: null,
		// AbortController for cancelling pending requests
		currentRequestController: null,
		sortingData: [
			{
				id: 'Name',
				title: 'Tag Name',
				property: 'Name',
				isSelected: true,
				isExpanded: false,
				isAsc: true,
			},
			{
				id: 'CreateDate',
				property: 'CreateDate',
				title: 'Date Added',
				isSelected: false,
				isExpanded: false,
				isAsc: true,
			},
		],
	},
	getters: {
		GET_SEARCH_TEXT(state) {
			return state.searchText;
		},
		GET_ENTRIES(state) {
			return state.entries;
		},
		GET_TOTAL(state) {
			return state.total;
		},
		GET_LOADING(state) {
			return state.loading;
		},
		GET_UPDATE_LOADING(state) {
			return state.updateLoading;
		},
		GET_PAGE(state) {
			return state.page;
		},
		GET_ITEMS_PER_PAGE(state) {
			return state.itemsPerPage;
		},
		GET_PAGES(state) {
			return Math.ceil(state.total / state.itemsPerPage);
		},
		GET_SELECTED_ITEMS(state) {
			return state.selectedItems;
		},
		GET_LAST_OPENED_ITEM_ID(state) {
			return state.lastOpenedItemId;
		},
		GET_SORTING_DATA(state) {
			return state.sortingData;
		},
		GET_SORT_PROPERTY(state) {
			return state.sortingData.find((o) => o.isSelected).property;
		},
		GET_SORT_ORDER_ASC(state) {
			return state.sortingData.find((o) => o.isSelected).isAsc;
		},
		GET_CURRENT_REQUEST_CONTROLLER(state) {
			return state.currentRequestController;
		},
	},
	mutations: {
		SET_SEARCH_TEXT(state, val) {
			state.searchText = val;
		},
		SET_ENTRIES(state, val) {
			state.entries = val;
		},
		SET_TOTAL(state, val) {
			state.total = val;
		},
		SET_LOADING(state, val) {
			state.loading = val;
		},
		SET_UPDATE_LOADING(state, val) {
			state.updateLoading = val;
		},
		SET_PAGE(state, val) {
			state.page = val;
		},
		SET_ITEMS_PER_PAGE(state, val) {
			state.itemsPerPage = val;
		},
		SET_SELECTED_ITEMS(state, val) {
			state.selectedItems = val;
		},
		SET_LAST_OPENED_ITEM_ID(state, id) {
			state.lastOpenedItemId = id;
		},
		CLEAR_SELECTED_ITEMS(state) {
			state.selectedItems = [];
			state.entries.forEach(item => {
				if (item.isSelected) {
					item.isSelected = false;
				}
			});
		},
		UPDATE_SELECTED_ITEMS(state) {
			state.selectedItems = state.entries.filter(item => item.isSelected);
		},
		SET_SORTING_DATA(state, val) {
			state.sortingData = val;
		},
		SET_REQUEST_CONTROLLER(state, controller) {
			state.currentRequestController = controller;
		},
		CLEAR_REQUEST_CONTROLLER(state) {
			state.currentRequestController = null;
		},
		RESET_LIST_STATE(state) {
			state.entries = [];
			state.total = 0;
			state.page = 1;
			state.itemsPerPage = 10;
			state.selectedItems = [];
			state.lastOpenedItemId = null;
			state.searchText = "";
			state.sortingData = [
				{
					id: 'Name',
					title: 'Tag Name',
					property: 'Name',
					isSelected: true,
					isExpanded: false,
					isAsc: true,
				},
				{
					id: 'CreateDate',
					property: 'CreateDate',
					title: 'Date Added',
					isSelected: false,
					isExpanded: false,
					isAsc: true,
				},
			];
		},
	},
	actions: {
		GET({ commit, state }, { refresh, params }) {
			// Abort any in-flight request; the newest request wins. The active
			// controller is the "latest request" token for the guards below.
			if (state.currentRequestController) {
				state.currentRequestController.abort();
			}
			const controller = new AbortController();
			commit('SET_REQUEST_CONTROLLER', controller);

			let options = {
				...params
			}

			if (refresh) {
				commit('SET_ENTRIES', []);
				commit('SET_TOTAL', 0);
				options = {
					...params,
					page: 1
				}
			}

			commit('SET_LOADING', true);

			operationService
				.get(options, controller.signal)
				.then(({ data }) => {
					// Ignore a superseded response — only the latest request commits.
					if (state.currentRequestController !== controller) return;

					data.items = data.items.map(t => {

						if (t.type === 'Annotation') {
							t = Object.assign(new ForgeAnnotation(), t);
						} else {
							t = Object.assign(new ForgeScaffold(), t);
						}

						t.parseContent();

						return t;
					})

					commit('SET_ENTRIES', data.items);

					// if (refresh) {
					// 	commit('SET_ENTRIES', data.items);
					// } else {
					// 	commit('SET_ENTRIES', [...state.entries, ...data.items]);
					// }

					commit('SET_TOTAL', data.total);
				})
				.catch((err) => {
					// Ignore cancellations (axios: CanceledError / ERR_CANCELED).
					if (isAbortError(err)) return;
					$handleError(err);
				})
				.finally(() => {
					// Only the latest request clears the shared controller/loading;
					// clear loading when idle so a superseded request can't lock it on.
					if (state.currentRequestController === controller) {
						commit('CLEAR_REQUEST_CONTROLLER');
					}
					if (state.currentRequestController === null) {
						commit('SET_LOADING', false);
					}
				});
		},

		GET_BY_ID(context, { type, id }) {
			return operationService.getById(type, id).then(({ data }) => {
				let t = data;
				t = type === 'General' ? Object.assign(new ForgeAnnotation(), t) : Object.assign(new ForgeScaffold(), t);
				t.parseContent();
				return t;
			});
		},
		UPDATE({ state, commit }, data) {
			commit('SET_UPDATE_LOADING', true);
			return operationService
				.update(data)
				.then((res) => {
					$updateOrAdd(state.entries, res.data);
					Vue.prototype.$dialog.notify.success(
						`Tag Data Successfully ${data.id ? 'Updated' : 'Added'}.`,
					);
					if (!data.id) {
						commit('SET_TOTAL', ++state.total);
					}
				})
				.catch((err) => {
					$handleError(err);
				})
				.finally(() => {
					commit('SET_UPDATE_LOADING', false);
				});
		},
		DELETE({ state, commit }, id) {
			return operationService
				.del(id)
				.then(() => {
					$deleteFromArray(state.entries, id);
					commit('SET_TOTAL', --state.total);
					Vue.prototype.$dialog.notify.success('Deleted Successfully', {
						position: 'top-right',
						timeout: 3000,
					});
				})
				.catch((err) => {
					$handleError(err);
				});
		},
		RESET_FOR_TAG_TYPE_ENTRY({ commit }) {
			commit('RESET_LIST_STATE');
		},
		UPDATE_SELECTION({ commit }) {
			commit('UPDATE_SELECTED_ITEMS');
		},
		CLEAR_SELECTION({ commit }) {
			commit('CLEAR_SELECTED_ITEMS');
		},
		/**
		 * Cancel any pending request
		 */
		CANCEL_CURRENT_REQUEST({ commit, state }) {
			if (state.currentRequestController) {
				state.currentRequestController.abort();
				commit('CLEAR_REQUEST_CONTROLLER');
				commit('SET_LOADING', false);
			}
		},
	}
}